"use client"

import * as React from "react"
import { AlertDialog as AlertDialogPrimitive } from "radix-ui"

import { cn } from "@/custolink-newdesign/lib/utils"
import { Button } from "@/custolink-newdesign/ui/button"

function AlertDialog({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Root>) {
  return <AlertDialogPrimitive.Root data-slot="alert-dialog" {...props} />
}

function AlertDialogTrigger({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Trigger>) {
  return (
    <AlertDialogPrimitive.Trigger data-slot="alert-dialog-trigger" {...props} />
  )
}

function AlertDialogPortal({
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Portal>) {
  return (
    <AlertDialogPrimitive.Portal data-slot="alert-dialog-portal" {...props} />
  )
}

function AlertDialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Overlay>) {
  return (
    <AlertDialogPrimitive.Overlay
      data-slot="alert-dialog-overlay"
      className={cn(
        "cl:fixed cl:inset-0 cl:z-50 cl:bg-black/50 cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogContent({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Content> & {
  size?: "default" | "sm"
}) {
  return (
    <AlertDialogPortal>
      <AlertDialogOverlay />
      {/* grid-cols-[minmax(0,1fr)]: see DialogContent - keeps long content
          inside the box instead of widening the implicit auto column. */}
      <AlertDialogPrimitive.Content
        data-slot="alert-dialog-content"
        data-size={size}
        className={cn(
          "cl:group/alert-dialog-content cl:fixed cl:top-[50%] cl:left-[50%] cl:z-50 cl:grid cl:grid-cols-[minmax(0,1fr)] cl:w-full cl:max-w-[calc(100%-2rem)] cl:translate-x-[-50%] cl:translate-y-[-50%] cl:gap-4 cl:rounded-lg cl:border cl:bg-background cl:p-6 cl:shadow-lg cl:duration-200 cl:data-[size=sm]:max-w-xs cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=closed]:zoom-out-95 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0 cl:data-[state=open]:zoom-in-95 cl:data-[size=default]:sm:max-w-lg",
          className
        )}
        {...props}
      />
    </AlertDialogPortal>
  )
}

function AlertDialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-header"
      className={cn(
        "cl:grid cl:grid-rows-[auto_1fr] cl:place-items-center cl:gap-1.5 cl:text-center cl:has-data-[slot=alert-dialog-media]:grid-rows-[auto_auto_1fr] cl:has-data-[slot=alert-dialog-media]:gap-x-6 cl:sm:group-data-[size=default]/alert-dialog-content:place-items-start cl:sm:group-data-[size=default]/alert-dialog-content:text-left cl:sm:group-data-[size=default]/alert-dialog-content:has-data-[slot=alert-dialog-media]:grid-rows-[auto_1fr]",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-footer"
      className={cn(
        "cl:flex cl:flex-col-reverse cl:gap-2 cl:group-data-[size=sm]/alert-dialog-content:grid cl:group-data-[size=sm]/alert-dialog-content:grid-cols-2 cl:sm:flex-row cl:sm:justify-end",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Title>) {
  return (
    <AlertDialogPrimitive.Title
      data-slot="alert-dialog-title"
      className={cn(
        "cl:text-lg cl:font-semibold cl:sm:group-data-[size=default]/alert-dialog-content:group-has-data-[slot=alert-dialog-media]/alert-dialog-content:col-start-2",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Description>) {
  return (
    <AlertDialogPrimitive.Description
      data-slot="alert-dialog-description"
      className={cn("cl:text-sm cl:text-muted-foreground", className)}
      {...props}
    />
  )
}

function AlertDialogMedia({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="alert-dialog-media"
      className={cn(
        "cl:mb-2 cl:inline-flex cl:size-16 cl:items-center cl:justify-center cl:rounded-md cl:bg-muted cl:sm:group-data-[size=default]/alert-dialog-content:row-span-2 cl:*:[svg:not([class*='size-'])]:size-8",
        className
      )}
      {...props}
    />
  )
}

function AlertDialogAction({
  className,
  variant = "default",
  size = "default",
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Action> &
  Pick<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <Button variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Action
        data-slot="alert-dialog-action"
        className={cn(className)}
        {...props}
      />
    </Button>
  )
}

function AlertDialogCancel({
  className,
  variant = "outline",
  size = "default",
  ...props
}: React.ComponentProps<typeof AlertDialogPrimitive.Cancel> &
  Pick<React.ComponentProps<typeof Button>, "variant" | "size">) {
  return (
    <Button variant={variant} size={size} asChild>
      <AlertDialogPrimitive.Cancel
        data-slot="alert-dialog-cancel"
        className={cn(className)}
        {...props}
      />
    </Button>
  )
}

export {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogMedia,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
}
